(() => {
  const elementName = "fifth-avenue-linked-products";
  if (customElements.get(elementName)) return;

  const normalize = (value) => String(value ?? "").trim().toLowerCase();
  const variantOptions = (variant) => Array.isArray(variant?.options) ? variant.options : [];
  const productScope = (element) => element?.closest?.(".product") || element?.closest?.("quick-add-modal, section[id^='MainProduct-']");

  class LinkedProducts extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      try {
        this.config = JSON.parse(this.querySelector("[data-linked-products-config]").textContent);
        this.choices = [...this.querySelectorAll("[data-linked-product-option]")].map((element) => ({
          element,
          product: JSON.parse(element.querySelector("[data-linked-product-data]").textContent),
        }));
      } catch {
        return;
      }
      this.currentProduct = this.choices.find(({ product }) => String(product.id) === String(this.config.productId))?.product;
      if (!this.currentProduct) return;
      this.colorNames = new Set(this.config.colorOptionNames.map(normalize).filter(Boolean));
      this.scope = productScope(this) || this.parentElement;
      const picker = this.scope.querySelector("variant-radios, variant-selects");
      this.sectionId = picker?.dataset.section;
      this.onVariantChange = (event) => {
        const detail = event.detail || {};
        if (detail.formElement) {
          if (productScope(detail.formElement) !== this.scope) return;
        } else {
          if (!this.sectionId || String(detail.sectionId) !== this.sectionId) return;
          if (!this.currentProduct.variants.some((variant) => String(variant.id) === String(detail.variant?.id))) return;
        }
        if (detail.variant && !this.currentProduct.variants.some((variant) => String(variant.id) === String(detail.variant.id))) return;
        this.update(detail.variant);
      };
      this.onOptionChange = (event) => {
        const picker = event.target.closest?.("variant-radios, variant-selects");
        if (!picker || productScope(picker) !== this.scope) return;
        queueMicrotask(() => {
          if (this.isConnected) this.update(picker.currentVariant || null);
        });
      };
      this.onClick = (event) => {
        const choice = event.target.closest?.("[data-linked-product-option]");
        if (choice && this.contains(choice) && !choice.hasAttribute("href")) event.preventDefault();
      };
      document.addEventListener("variant:change", this.onVariantChange);
      this.scope.addEventListener("change", this.onOptionChange);
      this.addEventListener("click", this.onClick);
      this.initialized = true;
      this.update(picker?.currentVariant || this.config.selectedVariant);
    }

    disconnectedCallback() {
      document.removeEventListener("variant:change", this.onVariantChange);
      this.scope?.removeEventListener("change", this.onOptionChange);
      this.removeEventListener("click", this.onClick);
      this.initialized = false;
    }

    findMatch(target, selectedVariant) {
      if (!selectedVariant) return {};
      const source = this.currentProduct;
      if (source.hasOnlyDefaultVariant !== target.hasOnlyDefaultVariant) return {};
      if (source.hasOnlyDefaultVariant && target.hasOnlyDefaultVariant) {
        return { variant: target.variants.find((variant) => variant.available), soldOut: target.variants.length > 0 };
      }
      const selectedOptions = variantOptions(selectedVariant);
      if (selectedOptions.length !== source.options.length) return {};
      const sourceOptions = source.options.map((name, index) => ({ name: normalize(name), value: normalize(selectedOptions[index]) }))
        .filter(({ name }) => !this.colorNames.has(name));
      const targetOptions = target.options.map((name, index) => ({ name: normalize(name), index }))
        .filter(({ name }) => !this.colorNames.has(name));
      if (sourceOptions.length !== targetOptions.length) return {};
      if (new Set(sourceOptions.map(({ name }) => name)).size !== sourceOptions.length) return {};
      if (new Set(targetOptions.map(({ name }) => name)).size !== targetOptions.length) return {};
      if (!sourceOptions.every(({ name }) => targetOptions.some((option) => option.name === name))) return {};
      const matches = target.variants.filter((variant) => {
        const values = variantOptions(variant);
        return values.length === target.options.length && sourceOptions.every(({ name, value }) => {
          const option = targetOptions.find((candidate) => candidate.name === name);
          return normalize(values[option.index]) === value;
        });
      });
      return { variant: matches.find((variant) => variant.available), soldOut: matches.length > 0 };
    }

    update(selectedVariant) {
      this.choices.forEach(({ element, product }) => {
        if (String(product.id) === String(this.currentProduct.id)) return;
        const { variant, soldOut } = this.findMatch(product, selectedVariant);
        if (variant) {
          const url = new URL(product.url, window.location.origin);
          url.searchParams.set("variant", variant.id);
          element.setAttribute("href", `${url.pathname}${url.search}${url.hash}`);
          element.removeAttribute("aria-disabled");
          element.setAttribute("aria-label", element.dataset.productTitle);
        } else {
          element.removeAttribute("href");
          element.setAttribute("aria-disabled", "true");
          const status = soldOut ? this.dataset.soldOutLabel : this.dataset.unavailableLabel;
          element.setAttribute("aria-label", `${element.dataset.productTitle} - ${status}`);
        }
      });
    }
  }

  customElements.define(elementName, LinkedProducts);
})();
